var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import * as atQuiz from '@codegrade/autotest-quiz';
import * as deque from '@codegrade/deque';
import { Maybe } from '@codegrade/maybe';
import { assert, expr, itertools, object, path as pathUtils, sort } from '@codegrade/utils';
import { CacheDecorator } from '@/utils/cache';
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
const toNullable = (el) => el;
/** Model for the assignment description. */
export class AssignmentDescriptionFile {
    assignment;
    /** This is an assignment description. */
    tag = 'assignment-description';
    /**
     * @param assignment The assignment to get the description for.
     */
    constructor(assignment) {
        this.assignment = assignment;
        this.documentId = `assignment-description-${this.assignment.id}`;
    }
    /** Unique identifier for the assignment description. */
    documentId;
    /** @inheritdoc */
    isViewable = true;
    /** @inheritdoc */
    isEditable = false;
    /** @inheritdoc */
    isDeletable = false;
    /** @inheritdoc */
    displayName = 'Description';
    /** @inheritdoc */
    name = `${this.displayName}.md`;
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
class BaseFileTreeFile {
    name;
    parent;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    _cache = new CacheDecorator();
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(name, parent) {
        this.name = name;
        this.parent = parent;
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    equals(other) {
        return this.path === other.path;
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get path() {
        return `${this.parent.path}/${this.name}`;
    }
    get parents() {
        const res = [];
        let cur = this.parent;
        while (cur != null) {
            res.push(cur);
            cur = cur.parent.extract();
        }
        return res.reverse();
    }
    /** The name to display for this file. */
    get displayName() {
        return this.name;
    }
}
__decorate([
    CacheDecorator.cache
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
], BaseFileTreeFile.prototype, "parents", null);
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class BinaryFileTreeFile extends BaseFileTreeFile {
    name;
    parent;
    /** This is a binary file. */
    tag = 'binary-file';
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(name, parent) {
        super(name, parent);
        this.name = name;
        this.parent = parent;
        Object.freeze(this);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static fromResponse(item, parent) {
        return new BinaryFileTreeFile(item.name, parent);
    }
    /** Binary files cannot be viewed in the code editor. */
    isViewable = false;
    /** Binary files are not editable in the code editor. */
    isEditable = false;
    /** Binary files can be deleted. */
    isDeletable = true;
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class TextFileTreeFile extends BaseFileTreeFile {
    name;
    documentId;
    parent;
    /** This is a text file. */
    tag = 'text-file';
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(name, documentId, parent) {
        super(name, parent);
        this.name = name;
        this.documentId = documentId;
        this.parent = parent;
        Object.freeze(this);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static fromResponse(item, parent) {
        return new TextFileTreeFile(item.name, item.documentId, parent);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static empty(name, parent) {
        return new TextFileTreeFile(name, '', parent);
    }
    /** Text files can be viewed in the code editor. */
    isViewable = true;
    /** Text files can be edited in the code editor. */
    isEditable = true;
    /** Text files can be deleted in the code editor. */
    isDeletable = true;
}
/** This class represents CodeGrade quiz files. */
export class CGQuizFileTreeFile extends BaseFileTreeFile {
    name;
    documentId;
    parent;
    /** This is a CodeGrade quiz file. */
    tag = 'cg-quiz';
    /**
     * @param name The name of the quiz.
     * @param documentId The id of the document in the backend.
     * @param parent The parent directory of this file.
     */
    constructor(name, documentId, parent) {
        super(name, parent);
        this.name = name;
        this.documentId = documentId;
        this.parent = parent;
        Object.freeze(this);
    }
    /**
     * Create a new instance of this class from websocket response.
     *
     * @param item The response data.
     * @param parent The parent directory of this file.
     * @returns A new instance of this class.
     */
    static fromResponse(item, parent) {
        return new CGQuizFileTreeFile(item.name, item.documentId, parent);
    }
    /** Quiz files can be viewed in the code editor. */
    isViewable = true;
    /** Quiz files can be edited in the code editor. */
    isEditable = true;
    /** Quiz files may not be deleted. */
    isDeletable = false;
    /** @inheritdoc */
    get displayName() {
        return atQuiz.cleanupQuizName(this.name);
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class FileTreeDir {
    name;
    parent;
    /** This is a directory. */
    tag = 'directory';
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    _cache = new CacheDecorator();
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    entries;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(name, parent) {
        this.name = name;
        this.parent = parent;
        this.entries = new Map();
    }
    /** Directories cannot be viewed in the code editor. */
    isViewable = false;
    /** Directories cannot be edited in the code editor. */
    isEditable = false;
    /** Directories can be deleted (though only if empty). */
    isDeletable = true;
    /** @inheritdoc */
    get displayName() {
        return this.name;
    }
    get parents() {
        return this.parent.caseOf({
            Nothing: () => [],
            Just: parent => [...parent.parents, parent],
        });
    }
    /* eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation. */
    _setEntries(entries) {
        entries.forEach(item => this.entries.set(item.name, item));
        Object.freeze(this);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    equals(other) {
        return this.path === other.path;
    }
    get path() {
        return this.parent.caseOf({
            Just: parent => `${parent.path}/${this.name}`,
            Nothing: () => '',
        });
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get empty() {
        return this.entries.size === 0;
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get elements() {
        return this.entries.values();
    }
    /** @returns The entries of this directory, sorted by their name. */
    get sortedElements() {
        return sort.sortBy(this.entries.values(), value => [value.name]);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    isAnyChild(file) {
        return file.parents.some(this.equals.bind(this));
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    getDirectChild(name) {
        return Maybe.fromNullable(this.entries.get(name));
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static fromTree(fileTree, parent) {
        const self = new FileTreeDir(fileTree.name, parent);
        const entries = [];
        for (const subItem of fileTree.entries.values()) {
            switch (subItem.type) {
                case 'text-file':
                    entries.push(TextFileTreeFile.fromResponse(subItem, self));
                    break;
                case 'binary-file':
                    entries.push(BinaryFileTreeFile.fromResponse(subItem, self));
                    break;
                case 'cg-quiz':
                    entries.push(CGQuizFileTreeFile.fromResponse(subItem, self));
                    break;
                case 'dir':
                    entries.push(FileTreeDir.fromTree(subItem, Maybe.of(self)));
                    break;
                default:
                    return assert.AssertionError.assertNever(subItem);
            }
        }
        // eslint-disable-next-line no-underscore-dangle
        self._setEntries(entries);
        return self;
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static empty(path) {
        return new FileTreeDir(path, Maybe.empty());
    }
}
__decorate([
    CacheDecorator.cache
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
], FileTreeDir.prototype, "parents", null);
__decorate([
    CacheDecorator.cache
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
], FileTreeDir.prototype, "path", null);
__decorate([
    CacheDecorator.cache
], FileTreeDir.prototype, "sortedElements", null);
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class FileTree {
    root;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(root) {
        this.root = root;
        Object.freeze(this);
    }
    /**
     * Get the first editable file in this tree.
     *
     * This does a breadth first search through the file tree and returns the first
     * text or quiz file, if any is found.
     *
     * @returns The first text or quiz file in this tree.
     */
    get firstFile() {
        const dirs = new deque.Deque([]);
        for (let dir = toNullable(this.root); dir != null; dir = dirs.shift()) {
            for (const el of dir.elements) {
                if (el.isEditable) {
                    return Maybe.of(el);
                }
                else if (el instanceof FileTreeDir) {
                    dirs.push(el);
                }
            }
        }
        return Maybe.empty();
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get hasFiles() {
        return itertools.some(this.getEachFile(), () => true);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static fromResponse(fileTree) {
        const root = {
            name: '',
            type: 'dir',
            entries: new Map(),
        };
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        const appendDir = (dir, name) => {
            let res = dir.entries.get(name);
            if (res == null || res.type !== 'dir') {
                res = {
                    name,
                    type: 'dir',
                    entries: new Map(),
                };
                dir.entries.set(name, res);
                return res;
            }
            return res;
        };
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        const appendFile = (dir, file) => {
            const res = dir.entries.get(file.name);
            if (res == null) {
                dir.entries.set(file.name, file);
            }
        };
        for (const item of fileTree) {
            let cur = root;
            const elements = pathUtils.splitPath(item.path);
            const last = elements.pop();
            if (last == null) {
                continue;
            }
            for (const el of elements) {
                cur = appendDir(cur, el);
            }
            switch (item.type) {
                case 'dir':
                    appendDir(cur, last);
                    break;
                case 'text-file':
                    appendFile(cur, {
                        type: 'text-file',
                        documentId: item.documentId,
                        name: last,
                    });
                    break;
                case 'binary-file':
                    appendFile(cur, {
                        type: 'binary-file',
                        name: last,
                    });
                    break;
                case 'cg-quiz':
                    appendFile(cur, {
                        type: 'cg-quiz',
                        documentId: item.documentId,
                        name: last,
                    });
                    break;
                default:
                    assert.AssertionError.assertNever(item);
            }
        }
        return new FileTree(FileTreeDir.fromTree(root, Maybe.empty()));
    }
    /**
     * Get the file at the given path, if it is editable.
     *
     * If the file is not editable, this function will return `Nothing`. Otherwise
     * it wraps the found file in a `Just`.
     *
     * @param path The path to look up.
     * @returns The file at the given path, if it is editable.
     */
    getEditableFile(path) {
        let cur = this.root;
        const parts = pathUtils.splitPath(path);
        const last = parts.pop();
        if (last == null) {
            return Maybe.empty();
        }
        for (const part of parts) {
            const newDir = cur.getDirectChild(part).extract();
            if (newDir == null || newDir instanceof BaseFileTreeFile) {
                return Maybe.empty();
            }
            cur = newDir;
        }
        return cur.getDirectChild(last).chain(f => {
            if (f.isEditable) {
                return Maybe.of(f);
            }
            return Maybe.empty();
        });
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    getItem(path) {
        let cur = this.root;
        const parts = pathUtils.splitPath(path);
        const last = parts.pop();
        if (last == null) {
            return Maybe.empty();
        }
        for (const part of parts) {
            const newDir = cur.getDirectChild(part).extract();
            if (newDir == null || newDir instanceof BaseFileTreeFile) {
                return Maybe.empty();
            }
            cur = newDir;
        }
        return cur.getDirectChild(last);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    addItem(...items) {
        const newITree = this.itree.concat(...items);
        return FileTree.fromResponse(newITree);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    deleteItem(...items) {
        const toDeletePaths = new Set(items.map(object.attr('path')));
        const newITree = this.itree.filter(it => !toDeletePaths.has(it.path));
        return FileTree.fromResponse(newITree);
    }
    /** @returns This file tree but flattened to a single list. */
    get itree() {
        const todo = [];
        const result = [];
        for (let entries = toNullable(this.root.elements); entries != null; entries = todo.pop()) {
            for (const cur of entries) {
                expr.match(cur, 'tag', {
                    'binary-file': ({ path }) => result.push({
                        type: 'binary-file',
                        path,
                    }),
                    'text-file': ({ path, documentId }) => result.push({
                        type: 'text-file',
                        path,
                        documentId,
                    }),
                    'cg-quiz': ({ path, documentId }) => result.push({
                        type: 'cg-quiz',
                        path,
                        documentId,
                    }),
                    directory: ({ path, elements }) => {
                        todo.push(elements);
                        result.push({
                            type: 'dir',
                            path,
                        });
                    },
                });
            }
        }
        return result;
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    *getEachFile() {
        const todo = [this.root.elements];
        while (true) {
            const entries = todo.pop();
            if (entries == null) {
                break;
            }
            for (const cur of entries) {
                if (cur instanceof BaseFileTreeFile) {
                    yield cur;
                }
                else {
                    todo.push(cur.elements);
                }
            }
        }
    }
}
